/* Community Deal-Sharing Wall — Layer 1.
 *
 * Three tabs:
 *   • Browse — paginated feed of masked listings across the platform
 *   • My Listings — what the user has shared, with the interest inbox
 *   • My Interests — what the user has expressed interest in
 *
 * Sellers click "Accept" on an interested buyer to:
 *   1. close the listing
 *   2. reveal the real address to that buyer
 *   3. unlock a "Draft DocuSign Assignment" CTA that pre-fills both sides
 *
 * Gated to paid tiers (free users see the upgrade CTA instead).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users2, MapPin, BedDouble, Bath, Ruler, DollarSign, Eye,
  HandshakeIcon, CheckCircle2, XCircle, Loader2, ArrowRight, Send,
  Search, Lock, Unlock, Sparkles, Inbox as InboxIcon, Crown,
  FileSignature, BellRing,
} from "lucide-react";
import SendForSignatureModal from "@/components/SendForSignatureModal";

// ── Envelope status badge — shown on each "My Listings" card when the seller
// has already sent a DocuSign Assignment envelope. Lets them see at a glance
// whether the buyer is dragging their feet, with a one-click Nudge button to
// re-send the DocuSign notification (throttled to once per 24h server-side).
const ENV_STATUS_META = {
  sent:        { label: "Sent",        cls: "text-sky-700 bg-sky-50 border-sky-200" },
  delivered:   { label: "Delivered",   cls: "text-sky-700 bg-sky-50 border-sky-200" },
  viewed:      { label: "Viewed",      cls: "text-amber-700 bg-amber-50 border-amber-200" },
  signed:      { label: "Signed",      cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  completed:   { label: "Completed",   cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  declined:    { label: "Declined",    cls: "text-rose-700 bg-rose-50 border-rose-200" },
  voided:      { label: "Voided",      cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  created:     { label: "Drafted",     cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
};

const NUDGEABLE = new Set(["sent", "delivered", "viewed"]);

function envTimeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function EnvelopeStatusBadge({ envelope, listingId, onChanged }) {
  const [nudging, setNudging] = useState(false);
  const meta = ENV_STATUS_META[envelope.status] || ENV_STATUS_META.created;
  const stamp = envelope.last_event_at || envelope.created_at;
  const canNudge = NUDGEABLE.has(envelope.status);
  const reminderCount = envelope.reminder_count || 0;
  const lastReminded = envelope.last_reminded_at;

  const nudge = async (e) => {
    e.stopPropagation();
    setNudging(true);
    try {
      await api.post(`/docusign/envelopes/${envelope.envelope_id}/remind`);
      toast.success(reminderCount > 0 ? "Reminder re-sent" : "Buyer nudged via DocuSign");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send reminder");
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="mb-3 flex items-center gap-2 flex-wrap">
      <div
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] font-semibold ${meta.cls}`}
        title={`Envelope ${envelope.envelope_id?.slice(0, 8) || ""} — last event ${stamp ? new Date(stamp).toLocaleString() : "unknown"}`}
        data-testid={`listing-envelope-status-${listingId}`}
      >
        <FileSignature size={11} /> DocuSign: {meta.label}
        {stamp && <span className="font-mono opacity-75 ml-1">· {envTimeAgo(stamp)}</span>}
      </div>
      {canNudge && (
        <button
          type="button"
          onClick={nudge}
          disabled={nudging}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-semibold hover:bg-amber-100 disabled:opacity-50 disabled:cursor-wait transition"
          title={lastReminded
            ? `Last nudged ${envTimeAgo(lastReminded)} (${reminderCount} total). DocuSign throttles to once per 24h per envelope.`
            : "Re-send the DocuSign email to anyone who hasn't signed yet"}
          data-testid={`listing-envelope-nudge-${listingId}`}
        >
          {nudging ? <Loader2 size={11} className="animate-spin" /> : <BellRing size={11} />}
          {nudging ? "Nudging…" : reminderCount > 0 ? `Nudge again (${reminderCount})` : "Nudge"}
        </button>
      )}
    </div>
  );
}

const USD = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TIME_AGO = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const StatusPill = ({ status }) => {
  const tone = {
    open: "bg-emerald-100 text-emerald-800 border-emerald-200",
    paused: "bg-amber-100 text-amber-800 border-amber-200",
    closed: "bg-zinc-100 text-zinc-700 border-zinc-200",
  }[status] || "bg-zinc-100 text-zinc-700";
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone}`}>
      {status}
    </span>
  );
};

const ListingCard = ({ listing, onClick, ctaLabel = "View Details", onChanged }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`listing-${listing.id}`}
    className="text-left bg-white rounded-xl border border-[var(--line)] hover:border-emerald-400 hover:shadow-lg hover:-translate-y-0.5 transition p-5 flex flex-col group"
  >
    <div className="flex items-start justify-between mb-3 gap-2">
      <div className="flex items-center gap-1.5 text-[var(--ink-2)] text-xs">
        <MapPin size={12} />
        <span className="font-semibold uppercase tracking-wider">
          {[listing.city, listing.state, listing.zip_code].filter(Boolean).join(" · ") || "Unknown market"}
        </span>
      </div>
      <StatusPill status={listing.status} />
    </div>

    <div className="mb-3">
      <div className="flex items-center gap-1.5 text-sm font-mono text-[var(--ink)]" title="Address is masked until seller accepts your interest">
        {listing.address_revealed ? <Unlock size={12} className="text-emerald-600" /> : <Lock size={12} className="text-[var(--ink-2)]" />}
        <span className={listing.address_revealed ? "" : "text-[var(--ink-2)]"}>{listing.property_address}</span>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3 text-xs text-[var(--ink-2)] mb-4">
      <div className="flex items-center gap-1"><BedDouble size={12} /> {listing.bedrooms ?? "—"} bd</div>
      <div className="flex items-center gap-1"><Bath size={12} /> {listing.bathrooms ?? "—"} ba</div>
      <div className="flex items-center gap-1"><Ruler size={12} /> {listing.sqft ? `${listing.sqft.toLocaleString()} sqft` : "—"}</div>
    </div>

    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="bg-emerald-50 rounded-md p-2.5">
        <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Asking Fee</div>
        <div className="text-emerald-900 font-extrabold text-lg leading-tight">{USD(listing.asking_assignment_fee)}</div>
      </div>
      <div className="bg-zinc-50 rounded-md p-2.5">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">ARV</div>
        <div className="text-[var(--ink)] font-extrabold text-lg leading-tight">{USD(listing.estimated_arv)}</div>
      </div>
    </div>

    {listing.share_notes && (
      <p className="text-xs text-[var(--ink-2)] line-clamp-2 italic mb-3">"{listing.share_notes}"</p>
    )}

    {listing.latest_envelope && (
      <EnvelopeStatusBadge envelope={listing.latest_envelope} listingId={listing.id} onChanged={onChanged} />
    )}

    <div className="mt-auto flex items-center justify-between pt-3 border-t border-[var(--line)]">
      <div className="text-[11px] text-[var(--ink-2)]">
        <span className="font-semibold text-[var(--ink-1)]">{listing.seller_display_name}</span>
        {listing.seller_reputation?.closed > 0 && (
          <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-700">
            <Sparkles size={9} />{listing.seller_reputation.closed} closed
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-[var(--ink-2)]">
        <Eye size={11} /> {listing.view_count}
        <HandshakeIcon size={11} /> {listing.interest_count}
        <ArrowRight size={12} className="text-emerald-600 group-hover:translate-x-0.5 transition" />
      </div>
    </div>
  </button>
);

// ─────────────────────── Browse tab
function BrowseTab({ onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");
  const [minFee, setMinFee] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (state) params.state = state.toUpperCase();
      if (minFee) params.min_fee = minFee;
      const { data } = await api.get("/community/listings", { params });
      setItems(data?.items || []);
    } catch {
      toast.error("Failed to load community wall");
    } finally {
      setLoading(false);
    }
  }, [state, minFee]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/community/listings");
        if (alive) setItems(data?.items || []);
      } catch {
        if (alive) toast.error("Failed to load community wall");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((i) =>
      [i.city, i.state, i.share_notes, i.property_type, i.seller_display_name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-white p-3 rounded-lg border border-[var(--line)]">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <Input
            className="pl-8"
            placeholder="Search city, notes, property type…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="community-search"
          />
        </div>
        <Input
          placeholder="State (e.g. FL)"
          value={state}
          maxLength={2}
          onChange={(e) => setState(e.target.value.toUpperCase())}
          data-testid="community-filter-state"
        />
        <Input
          type="number"
          placeholder="Min fee ($)"
          value={minFee}
          onChange={(e) => setMinFee(e.target.value)}
          data-testid="community-filter-min-fee"
        />
        <div className="sm:col-span-4 flex justify-end">
          <Button size="sm" variant="outline" onClick={load} data-testid="community-apply-filters">
            Apply filters
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--ink-2)]">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading the wall…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 px-6 border border-dashed border-[var(--line)] rounded-xl bg-white">
          <Users2 size={32} className="mx-auto text-[var(--ink-2)] mb-3" />
          <div className="font-display font-bold text-lg">The wall is quiet</div>
          <p className="text-sm text-[var(--ink-2)] mt-1.5 max-w-md mx-auto">
            Be the first to share a deal in your market. Open any lead in your pipeline and toggle
            "Share to Community" — your peers see a masked version and can express interest.
          </p>
          <Link to="/app/leads" className="btn-primary inline-flex items-center gap-1.5 mt-5">
            Open a lead <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((l) => (
            <ListingCard key={l.id} listing={l} onClick={() => onPick(l)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── My Listings tab (seller view)
function MyListingsTab({ onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/community/my-listings");
        if (alive) setItems(data?.items || []);
      } catch {
        if (alive) toast.error("Failed to load your listings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  if (loading) {
    return <div className="text-center py-12 text-[var(--ink-2)]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="text-center py-16 px-6 border border-dashed border-[var(--line)] rounded-xl bg-white">
        <InboxIcon size={32} className="mx-auto text-[var(--ink-2)] mb-3" />
        <div className="font-display font-bold text-lg">You haven't shared any deals yet</div>
        <p className="text-sm text-[var(--ink-2)] mt-1.5 max-w-md mx-auto">
          Open a lead and click "Share to Community" on the right rail to test the wall flow.
        </p>
        <Link to="/app/leads" className="btn-primary inline-flex items-center gap-1.5 mt-5">
          Pick a lead <ArrowRight size={14} />
        </Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-listings-grid">
      {items.map((l) => <ListingCard key={l.id} listing={l} onClick={() => onPick(l)} ctaLabel="Manage" onChanged={reload} />)}
    </div>
  );
}

// ─────────────────────── My Interests tab (buyer view)
function MyInterestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/community/my-interests");
        if (alive) setItems(data?.items || []);
      } catch {
        if (alive) toast.error("Failed to load your interests");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="text-center py-12 text-[var(--ink-2)]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="text-center py-16 px-6 border border-dashed border-[var(--line)] rounded-xl bg-white">
        <HandshakeIcon size={32} className="mx-auto text-[var(--ink-2)] mb-3" />
        <div className="font-display font-bold text-lg">You haven't claimed any deals yet</div>
        <p className="text-sm text-[var(--ink-2)] mt-1.5">Browse the wall and tap "I'll take it" to express interest.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="my-interests-list">
      {items.map((it) => (
        <div key={it.id} className="bg-white rounded-xl border border-[var(--line)] p-4 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                it.status === "accepted" ? "bg-emerald-100 text-emerald-800" :
                it.status === "declined" ? "bg-zinc-200 text-zinc-700" :
                "bg-amber-100 text-amber-800"
              }`}>{it.status}</span>
              <span className="text-xs text-[var(--ink-2)]">{TIME_AGO(it.created_at)}</span>
            </div>
            <div className="font-mono text-sm truncate">{it.listing?.property_address}</div>
            <div className="text-xs text-[var(--ink-2)] mt-1">
              {[it.listing?.city, it.listing?.state].filter(Boolean).join(", ")}
              {" · "}fee {USD(it.listing?.asking_assignment_fee)}
              {" · "}ARV {USD(it.listing?.estimated_arv)}
            </div>
          </div>
          {it.status === "accepted" && it.listing?.address_revealed && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
              <Unlock size={12} /> Address revealed
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────── Detail / Interest Modal
function ListingDetailModal({ open, onOpenChange, listingId, onChanged }) {
  const [listing, setListing] = useState(null);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  // ── envelope draft state ──────────────────────────────────────────────
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [envelopePrefill, setEnvelopePrefill] = useState(null);
  const [draftingEnvelope, setDraftingEnvelope] = useState(false);

  useEffect(() => {
    if (!open || !listingId) { setListing(null); setInterests([]); setMessage(""); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/community/listings/${listingId}`);
        if (!alive) return;
        setListing(data);
        if (data.is_mine) {
          const r = await api.get(`/community/listings/${listingId}/interests`);
          if (alive) setInterests(r.data?.items || []);
        }
      } catch {
        if (alive) toast.error("Failed to load listing");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, listingId]);

  const expressInterest = async () => {
    setBusy(true);
    try {
      await api.post(`/community/listings/${listingId}/interest`, { message: message || null });
      toast.success("Interest sent — the seller has been notified");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send interest");
    } finally {
      setBusy(false);
    }
  };

  const acceptBuyer = async (interestId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/community/interests/${interestId}/accept`);
      toast.success("Buyer accepted — address revealed to them. Envelope draft ready in Contracts.");
      setListing(data.listing);
      const r = await api.get(`/community/listings/${listingId}/interests`);
      setInterests(r.data?.items || []);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept");
    } finally {
      setBusy(false);
    }
  };

  const declineBuyer = async (interestId) => {
    setBusy(true);
    try {
      await api.post(`/community/interests/${interestId}/decline`);
      const r = await api.get(`/community/listings/${listingId}/interests`);
      setInterests(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't decline");
    } finally {
      setBusy(false);
    }
  };

  const openEnvelopeDraft = async () => {
    setDraftingEnvelope(true);
    try {
      const { data } = await api.get(
        `/community/listings/${listingId}/envelope-prefill`
      );
      setEnvelopePrefill(data);
      setSendModalOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't prepare envelope draft");
    } finally {
      setDraftingEnvelope(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="listing-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl flex items-center gap-2">
            {listing?.address_revealed ? <Unlock size={18} className="text-emerald-600" /> : <Lock size={18} />}
            Deal Details
          </DialogTitle>
        </DialogHeader>
        {loading || !listing ? (
          <div className="text-center py-10 text-[var(--ink-2)]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="bg-[var(--bg)] rounded-lg p-4 border border-[var(--line)]">
              <div className="flex items-center gap-1.5 text-xs text-[var(--ink-2)] mb-2">
                <MapPin size={12} />
                {[listing.city, listing.state, listing.zip_code].filter(Boolean).join(" · ")}
                <StatusPill status={listing.status} />
              </div>
              <div className="font-mono text-base">{listing.property_address}</div>
              {!listing.address_revealed && !listing.is_mine && (
                <p className="text-[11px] text-amber-700 mt-2 italic">
                  Address unlocks once the seller accepts your interest.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Beds" value={listing.bedrooms ?? "—"} />
              <Stat label="Baths" value={listing.bathrooms ?? "—"} />
              <Stat label="Sqft" value={listing.sqft?.toLocaleString() || "—"} />
              <Stat label="Year" value={listing.year_built || "—"} />
              <Stat label="Asking Price" value={USD(listing.asking_price)} />
              <Stat label="ARV" value={USD(listing.estimated_arv)} tone="zinc" />
              <Stat label="Repairs" value={USD(listing.estimated_repairs)} />
              <Stat label="Asking Fee" value={USD(listing.asking_assignment_fee)} tone="emerald" />
            </div>

            {listing.share_notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm italic text-amber-900">
                "{listing.share_notes}"
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-[var(--ink-2)] pt-2 border-t border-[var(--line)]">
              Shared by <span className="font-bold text-[var(--ink-1)]">{listing.seller_display_name}</span>
              {listing.seller_reputation?.closed > 0 && (
                <span className="inline-flex items-center gap-0.5 text-emerald-700">
                  <Sparkles size={10} />{listing.seller_reputation.closed} closed deals
                </span>
              )}
              <span className="ml-auto">{TIME_AGO(listing.created_at)}</span>
            </div>

            {/* CTA — buyer side */}
            {!listing.is_mine && listing.status === "open" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="font-semibold text-sm mb-2">Interested? Send the seller a quick note.</div>
                <Textarea
                  rows={3}
                  maxLength={1000}
                  placeholder="e.g. Active buyer in Hillsborough County. Can close cash in 7 days. Happy to wire EMD same-day."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="interest-message"
                />
                <div className="text-[10px] text-[var(--ink-2)] mt-1 text-right">{message.length}/1000</div>
                <Button onClick={expressInterest} disabled={busy} className="mt-2 w-full" data-testid="express-interest-btn">
                  {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <HandshakeIcon size={14} className="mr-2" />}
                  I'll take it
                </Button>
              </div>
            )}

            {/* CTA — buyer side (accepted) */}
            {!listing.is_mine && listing.address_revealed && listing.status === "closed" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle2 className="text-emerald-600" size={24} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">Address revealed</div>
                  <div className="text-xs text-[var(--ink-2)]">The seller will draft a DocuSign envelope on their side. Watch your inbox.</div>
                </div>
              </div>
            )}

            {/* Seller-only: interest inbox */}
            {listing.is_mine && (
              <div className="border-t border-[var(--line)] pt-4">
                <div className="text-xs uppercase tracking-wider font-bold text-[var(--ink-2)] mb-3">
                  Interested buyers ({interests.length})
                </div>
                {interests.length === 0 ? (
                  <div className="text-sm text-[var(--ink-2)] italic">No interest yet. Hang tight — fresh listings climb the wall first.</div>
                ) : (
                  <div className="space-y-2" data-testid="seller-interests">
                    {interests.map((i) => (
                      <div key={i.id} className="border border-[var(--line)] rounded-lg p-3 flex items-start gap-3 bg-white">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm">{i.buyer_display_name}</span>
                            {i.buyer_reputation?.closed > 0 && (
                              <span className="text-[10px] inline-flex items-center gap-0.5 text-emerald-700">
                                <Sparkles size={9} />{i.buyer_reputation.closed} closed
                              </span>
                            )}
                            <span className="text-[10px] text-[var(--ink-2)] ml-auto">{TIME_AGO(i.created_at)}</span>
                          </div>
                          {i.message && <p className="text-xs text-[var(--ink-1)] mt-1 italic">"{i.message}"</p>}
                          {i.status !== "pending" && (
                            <span className={`text-[10px] font-bold uppercase tracking-wider mt-2 inline-block ${i.status === "accepted" ? "text-emerald-700" : "text-zinc-500"}`}>
                              {i.status}
                            </span>
                          )}
                        </div>
                        {i.status === "pending" && listing.status === "open" && (
                          <div className="flex flex-col gap-1.5 shrink-0">
                            <Button size="sm" onClick={() => acceptBuyer(i.id)} disabled={busy} data-testid={`accept-${i.id}`}>
                              <CheckCircle2 size={12} className="mr-1" /> Accept
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => declineBuyer(i.id)} disabled={busy} data-testid={`decline-${i.id}`}>
                              <XCircle size={12} className="mr-1" /> Decline
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {listing.status === "closed" && listing.accepted_buyer_user_id && listing.is_mine && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3">
                    <Sparkles className="text-emerald-600" size={20} />
                    <div className="text-sm flex-1">
                      <div className="font-semibold">Deal closed via community wall</div>
                      <div className="text-xs text-[var(--ink-2)]">Draft a DocuSign Assignment between you and the buyer to finalize.</div>
                    </div>
                    <Button
                      onClick={openEnvelopeDraft}
                      disabled={draftingEnvelope}
                      size="sm"
                      data-testid="community-draft-envelope-btn"
                    >
                      {draftingEnvelope
                        ? <><Loader2 size={12} className="mr-1 animate-spin" /> Loading…</>
                        : <><Send size={12} className="mr-1" /> Draft envelope</>}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {/* Pre-filled envelope draft modal — only mounted when the seller opens it. */}
      {envelopePrefill && (
        <SendForSignatureModal
          open={sendModalOpen}
          onOpenChange={(o) => {
            setSendModalOpen(o);
            if (!o) setEnvelopePrefill(null);
          }}
          lead={envelopePrefill.lead}
          prefill={envelopePrefill}
          onSent={() => {
            setSendModalOpen(false);
            setEnvelopePrefill(null);
            toast.success("Envelope sent — the buyer will receive it for signature.");
            onChanged?.();
          }}
        />
      )}
    </Dialog>
  );
}

const Stat = ({ label, value, tone = "" }) => (
  <div className={`p-3 rounded-md ${tone === "emerald" ? "bg-emerald-50" : tone === "zinc" ? "bg-zinc-50" : "bg-white border border-[var(--line)]"}`}>
    <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">{label}</div>
    <div className="text-[var(--ink)] font-extrabold text-base mt-0.5">{value}</div>
  </div>
);

// ─────────────────────── Free-tier upgrade gate
function UpgradeGate() {
  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="community-upgrade-gate">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-[var(--ink-2)] font-bold uppercase tracking-wider mb-2">
          <Users2 size={14} /> Community
        </div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Deal-Sharing Wall</h1>
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden">
        <div className="absolute top-4 right-4">
          <Crown className="text-amber-500" size={28} />
        </div>

        <Lock size={36} className="mx-auto text-emerald-600 mb-4" />
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl mb-3 text-[var(--ink)]">
          Paid feature — upgrade to unlock
        </h2>
        <p className="text-[var(--ink-1)] max-w-xl mx-auto mb-6 leading-relaxed">
          The Community Deal Wall connects you to other wholesalers across the platform.
          Share dead leads, find buyers in markets you don't serve, and let our DocuSign
          flow handle the assignment between you two — all without exchanging contact info
          until you're ready to close.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto mb-7 text-left">
          {[
            { icon: Users2, title: "Browse the wall", body: "See masked listings from wholesalers in your state." },
            { icon: HandshakeIcon, title: "Handshake to reveal", body: "Address unlocks when the seller accepts you." },
            { icon: Send, title: "Auto-draft envelope", body: "DocuSign Assignment pre-filled for both parties." },
          ].map((b) => (
            <div key={b.title} className="bg-white rounded-lg p-4 border border-emerald-200">
              <b.icon size={16} className="text-emerald-600 mb-2" />
              <div className="font-semibold text-sm mb-0.5">{b.title}</div>
              <div className="text-xs text-[var(--ink-2)]">{b.body}</div>
            </div>
          ))}
        </div>

        <Link
          to="/app/billing"
          className="btn-primary inline-flex items-center gap-2 px-6 py-3"
          data-testid="community-upgrade-cta"
        >
          <Crown size={16} /> See plans &amp; upgrade <ArrowRight size={14} />
        </Link>

        <p className="text-[11px] text-[var(--ink-2)] mt-4 italic">
          Available on Pro, Hustler, and Team plans.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────── Page
export default function Community() {
  const { user } = useAuth();
  const isPaid = (user?.tier && user.tier !== "free") || user?.role === "admin";

  const [tab, setTab] = useState("browse");
  const [detailId, setDetailId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const pick = useCallback((listing) => setDetailId(listing.id), []);
  const onChanged = useCallback(() => setReloadKey((k) => k + 1), []);

  if (!isPaid) return <UpgradeGate />;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="community-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-[var(--ink-2)] font-bold uppercase tracking-wider mb-2">
          <Users2 size={14} /> Community
        </div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Deal-Sharing Wall</h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          Turn dead leads into revenue. Share a masked version of any deal in your pipeline,
          let other wholesalers express interest, and accept the strongest buyer with one tap —
          we'll draft the DocuSign Assignment between you two automatically.
        </p>
      </div>

      <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-[var(--line)] w-fit mb-6" data-testid="community-tabs">
        {[
          { id: "browse", label: "Browse Wall" },
          { id: "mine", label: "My Listings" },
          { id: "interests", label: "My Interests" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`community-tab-${t.id}`}
            className={
              "px-4 py-1.5 rounded-md text-sm font-semibold transition " +
              (tab === t.id
                ? "bg-[var(--ink)] text-white shadow-sm"
                : "text-[var(--ink-2)] hover:text-[var(--ink)]")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "browse" && <BrowseTab key={`browse-${reloadKey}`} onPick={pick} />}
      {tab === "mine" && <MyListingsTab key={`mine-${reloadKey}`} onPick={pick} />}
      {tab === "interests" && <MyInterestsTab key={`int-${reloadKey}`} />}

      <ListingDetailModal
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        listingId={detailId}
        onChanged={onChanged}
      />
    </div>
  );
}
